import React, { useMemo, useState, useRef } from "react";
import { View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform
} from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import ReactCountryFlag from 'react-country-flag';
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import GradientBackground from '../../../../uiComponents/GradientBackground';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import {
  openMoneygramPrivacyPolicy,
  openMoneygramPrivacyPolicyUsEs,
  openMoneygramTermsAndConditions,
  openMoneygramTermsAndConditionsUsEs,
} from "../../../../../helper/moneygramLegalLinks";
import DownArrow from "../../../../../../../../assets/images/DownArrowIcon.svg";
import Colors from "../../../../../constants/colors";

type Props = {
  onContinue?: () => void;
  onEditStep?: (key: string) => void;
  testID?: string;
};

export default function BillPayReviewAndEditStep({ onEditStep, onContinue, testID }: Props) {
  const { t, i18n } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const inputs = useRef<(TextInput | null)[]>([]);
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const selectedCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedCountryName = useSessionStore((s: SessionState) => s.selectedDestinationCountryName);
  const selectedStateName = useSessionStore((s: SessionState) => s.selectedDestinationStateName);
  const selectedBillerInformation = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const billPayBillerSearchResponseStore = useSessionStore((s: SessionState) => (s as any).billPayBillerSearchResponseStore);
  const billPayFeeLookupResponseStore = useSessionStore((s: SessionState) => (s as any).billPayFeeLookupResponseStore);
  

  const option: any = pendingSendOption || selectedSendOption || null;

  const summary = useMemo(() => {
    if (!option) {
      return {
        sendAmount: 0,
        sendCurrency: 'USD',
        receiveAmount: 0,
        receiveCurrency: '—',
        exchangeRate: 0,
        fees: 0,
        total: 0,
      };
    }
    const sendCurrency: string = option?.SendAmounts?.SendCurrency || 'USD';
    const sendAmount: number = Number(option?.SendAmounts?.SendAmount || 0);
    const receiveCurrency: string = option?.ReceiveAmounts?.ValidCurrencyIndicator
      ? (option?.ValidReceiveCurrency || '')
      : (option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || '');
    const receiveAmount: number = Number(option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0);
    const exchangeRateNum: number = Number(FeeLookupHelper.getExchangeRate(option) || 0);
    const fees: number = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
    const totalBase: number = Number(option?.TotalAmount || 0);
    const total: number = totalBase;
    return { sendAmount, sendCurrency, receiveAmount, receiveCurrency, exchangeRate: exchangeRateNum, fees, total };
  }, [pendingSendOption, selectedSendOption]);

  const totalsFromFeeLookup = useMemo(() => {
    try {
      const raw: any = billPayFeeLookupResponseStore as any;

       // Normalize inputs that may be JSON strings
       const normalizedFeeLookup: any = (() => {
        let raw: any = billPayFeeLookupResponseStore as any;
        if (typeof raw === 'string') {
          try { raw = JSON.parse(raw); } catch {}
        }
        return raw;
      })();


      const container: any = raw?.FeeInfos?.[0] ?? raw?.feeInfos?.[0] ?? raw ?? {};
      const sendAmts: any = normalizedFeeLookup?.FeeInfos[0].SendAmounts ?? {};
      const totalFeesRaw: any = normalizedFeeLookup?.FeeInfos[0].SendAmounts.TotalSendFees
      ?? 0;
      const totalAmountRaw: any = normalizedFeeLookup?.FeeInfos[0].TotalAmount?? 0;
      const parseNum = (v: any) => {
        const s = String(v ?? '0');
        const n = Number(s.replace(/,/g, ''));
        return isFinite(n) ? n : 0;
      };
      return {
        totalFees: parseNum(totalFeesRaw),
        totalAmount: parseNum(totalAmountRaw),
      };
    } catch {
      return { totalFees: 0, totalAmount: 0 };
    }
  }, [billPayFeeLookupResponseStore]);

  const alpha2 = useMemo(() => {
      const a3 = (selectedCountryCode || '').toString().toUpperCase();
      return countries.alpha3ToAlpha2(a3) || '';
    }, [selectedCountryCode]);
  
    const countryCodeToEmoji = (code: string) => {
      if (!code) return "";
      const upper = code.toUpperCase();
      if (upper.length !== 2) return "";
      const base = 127397;
      return [...upper].map((c) => String.fromCodePoint(base + c.charCodeAt(0))).join("");
    };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>
      {/* <Text style={styles.subtitle}>{t("REVIEW_SUBTITLE")}</Text> */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{Number(String(currentSendAmount || '0').replace(/,/g, '')) .toFixed(2)}</Text>
            <Text style={styles.currencyText}>{summary.sendCurrency}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity testID="payAmountButton"
            style={styles.editButton}
            onPress={() => onEditStep?.('payAmount')}>
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel]}>{t("DESTINATION")}</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            {Platform.OS === 'web' ? (
            <View
              style={{
                width: 16,
                height: 16,
                borderRadius: 50,
                overflow: "hidden",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 8,
              }}
            >
              {alpha2 ? (
                <ReactCountryFlag
                  countryCode={alpha2}
                  svg
                  style={{ width: 30, height: 30, borderRadius: 50 }}
                  title={alpha2}
                />
              ) : (
                <Text style={{ fontSize: 16 }}>🌍</Text>
              )}</View>
            ) : (
              <Text style={{ fontSize: 16 }}>{countryCodeToEmoji(alpha2) || '🌍'}</Text>
            )}
            <Text style={styles.CountryText}>{String(selectedBillerInformation?.BillerName ?? selectedBillerInformation?.billerName ?? selectedCountryName)}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity testID="previosBillerInfoButton"
            // style={{ backgroundColor: Colors.white, height: 30, width: 50, alignItems: 'flex-end', justifyContent: 'center'}}
            // onPress={() => onEditStep?.('previosBillerInfo')}>
            // <DownArrow width={20} height={20}/>
            style={styles.editButton}
            onPress={() => onEditStep?.('previosBillerInfo')}>
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      </View>
      { (selectedCountryCode === 'USA' || selectedCountryCode === 'CAN') &&
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel]}>{t("DESTINATION_STATE")}</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            <Text style={styles.CountryText}>{selectedStateName}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity testID="stateButton"
            style={styles.editButton}
            onPress={() => onEditStep?.('state')}>
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      </View>
      }
      {/* Terms */}
      <View style={{ marginTop: 24, marginBottom: 16 }}>
        {
                    i18n.language === 'en' ? (
                      <View>
                        <Text style={styles.terms}>
                          I agree to MoneyGram's{" "}
                          <Text
                            style={[styles.link, { textDecorationLine: "underline" }]}
                            onPress={openMoneygramTermsAndConditions}
                            accessibilityRole="link"
                          >
                            Terms and Conditions
                          </Text>
                          {/* {", "}
                          <Text
                            style={[styles.link, { textDecorationLine: "underline" }]}
                            onPress={openMoneygramTermsOfUse}
                            accessibilityRole="link"
                          >
                            Terms of Use
                          </Text> */}
                          {" and confirm I am the sole beneficial owner of the assets, and am acting solely on my own profile. By continuing with this transaction I consent to the collection, use, disclosure, and transfer (including cross-border transfer) of my personal information as described in our "}
                          <Text
                            style={[styles.link, { textDecorationLine: "underline" }]}
                            onPress={openMoneygramPrivacyPolicy}
                            accessibilityRole="link"
                          >
                            Privacy Policy.
                          </Text>
                        </Text>
                        <Text style={[styles.terms, { marginTop: 16 }]}>
                          <Text style={styles.bold}>Exchange rate</Text>{"\n"}
                          In addition to transfer fees, a currency exchange rate, also determined by MoneyGram, may also apply. MoneyGram may also make money from currency exchange. When selecting a money transfer operator, carefully compare both transfer fees and currency exchange rates. Transfer fees may vary depending on the amount being transferred and the delivery method. Transfer fee and currency exchange rate may be subject to change by MoneyGram without notice.
                        </Text>
                      </View>
                    ) : (
                      <View>
                        <Text style={styles.terms}>
                          Acepto los{" "}
                          <Text
                            style={[styles.link, { textDecorationLine: "underline" }]}
                            onPress={openMoneygramTermsAndConditionsUsEs}
                            accessibilityRole="link"
                          >
                            Términos y Condiciones
                          </Text>
                          {" de MoneyGram y confirmo que soy el único propietario beneficiario de los activos, y actúo únicamente en mi propio perfil. Al continuar con esta transacción, consiento la recopilación, uso, divulgación y transferencia (incluida la transferencia transfronteriza) de mi información personal como se describe en nuestra "}
                          <Text
                            style={[styles.link, { textDecorationLine: "underline" }]}
                            onPress={openMoneygramPrivacyPolicyUsEs}
                            accessibilityRole="link"
                          >
                            Política de Privacidad.
                          </Text>
                        </Text>
                        <Text style={[styles.terms, { marginTop: 16 }]}>
                        <Text style={styles.bold}>Tipo de cambio</Text>{"\n"}
                        {t("TERMS_EXCHANGE_POLICY")}
                        </Text>
                      </View>
                    )
                  }
      </View>
      {/* Fees and CTA */}
       {/* <View><View style={styles.viewTap}/></View> */}
      {/* <GradientBackground style={styles.gradientCard}>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("AVAILABLE")}</Text>
          <View style={{ flexDirection: 'row'}}>
            {(() => {
              try {
                const raw: any = billPayBillerSearchResponseStore as any;
                const container: any = (raw?.BillerInfo ?? raw?.billerInfo ?? raw) as any;
                const item: any = (container?.BillerInfos?.[0] ?? container?.billerInfos?.[0] ?? container) as any;
                const notes: string = String(item?.BillerNotes ?? item?.billerNotes ?? '').trim();
                return <Text style={styles.oldPrice}>{notes || ' '}</Text>;
              } catch {
                return <Text style={styles.oldPrice}>{' '}</Text>;
              }
            })()}
          </View>
        </View>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_FEES")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{totalsFromFeeLookup.totalFees.toFixed(2)} {summary.sendCurrency}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <Text style={styles.feeLabel}>{t("REVIEW_TOTAL")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{totalsFromFeeLookup.totalAmount.toFixed(2)} {summary.sendCurrency}</Text>
          </View>
        </View>
        <TouchableOpacity testID="continueButton"
          onPress={onContinue}
          activeOpacity={0.8}
          style={styles.continueButton}
        >
          <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
        </TouchableOpacity>
      </GradientBackground> */}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    // justifyContent: "flex-start",
    // marginTop: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 6,
    color: "#111827",
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '400',
    fontFamily: 'Rubik',
    color: Colors.subTitleColor,
    lineHeight: 22,
    alignSelf: 'stretch'
  },
    exchangeRate: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '500',
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pickUpText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: '#000',
    lineHeight: 20
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: '#1f1f1f',
    lineHeight: 38
  },
  amountTextGreen: {
    color: '#4CAF50',
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: '#8C8C8C',
    lineHeight: 18,
    paddingLeft: 6
  },
  editButton: {
    backgroundColor: Colors.searchBackground,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14
  },
  editText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: Colors.modalTitle,
    lineHeight: 16
  },
  CountryText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: '#1F1F1F',
    lineHeight: 24,
    // paddingLeft: 6
  },
  readOnlyInput: {
    backgroundColor: '#fafafa',
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
  },
  pinContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  card: {
    alignItems: "center",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // position: "absolute",
    // width: 327,
  },
  gradientCard: {
    alignItems: "center",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    position: "absolute",
    right: 0,
    left: 0,
    top: "100%",
    transform: [{ translateX: -10 }],
  },
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius:24,
    marginBottom: 24,
    alignSelf: 'center'
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  terms: {
    fontSize: 12,
    color: Colors.subTitleColor,
    fontFamily: 'Rubik',
    fontWeight: '400',
    lineHeight: 16,
  },
  link: {
    color: Colors.titleTextColor,
    fontWeight: "500",
  },
  bold: { fontWeight: "600" },
  continueButton: {
    alignSelf: "center",
    backgroundColor: "#000",
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: "#000",
  },
  continueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: "#FFF",
    lineHeight: 24
  },
});
